import { gql } from '@apollo/client';

export const LIST_SECURITY_GROUPS = gql`
  query listSecurityGroups($cursor: Cursor) {
    listSecurityGroups(cursor: $cursor) {
      edges {
        node {
          id
          name
          description
          createdTime
          lastModifiedTime
          createdBy
          memberCount
          lastSeenTime
          members {
            userId
            email
            displayName
          }
        }
      }
      pageInfo {
        totalCount
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

export const GET_SECURITY_GROUP_DELETE_IMPACT = gql`
  query getSecurityGroupDeleteImpact($securityGroupId: ID!) {
    getSecurityGroupDeleteImpact(securityGroupId: $securityGroupId) {
      totalMembers
      usersWhoRetain {
        userId
        email
        displayName
      }
      usersWhoLoseAll {
        userId
        email
        displayName
      }
    }
  }
`;

export const LIST_SECURITY_GROUP_MEMBERS = gql`
  query listSecurityGroupMembers($securityGroupId: ID!, $cursor: Cursor) {
    listSecurityGroupMembers(securityGroupId: $securityGroupId, cursor: $cursor) {
      edges {
        node {
          userId
          email
          displayName
          source
          createdTime
          lastSeenTime
          accessibleAssetCount
        }
      }
      pageInfo {
        totalCount
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

export const LIST_ORG_USERS = gql`
  query listOrgUsers($cursor: Cursor) {
    listOrgUsers(cursor: $cursor) {
      edges {
        node {
          id
          orgId
          email
          emailVerified
          displayName
          status
          createdTime
          lastSeenTime
          lastModifiedTime
          accessibleAssetCount
          isExternal
          securityGroups {
            id
            name
            maxRolePrecedence
          }
          acrs {
            id
            roleId
            roleName
            resourceType
            resourceId
            resourceName
            entityType
            entityName
          }
          permissionCount
        }
      }
      pageInfo {
        totalCount
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

// Shared cursor for LIST_ACRS so the initial fetch and every refetchQueries entry stay in lockstep.
export const RBAC_LIST_CURSOR = { first: 500 };

export const LIST_ACRS = gql`
  query listACRs($userId: ID, $cursor: Cursor) {
    listACRs(userId: $userId, cursor: $cursor) {
      edges {
        node {
          id
          orgId
          entityType
          entityId
          entityName
          roleId
          roleName
          resourceType
          resourceId
          resourceName
          createdTime
          createdBy
          expiresAt
          directPermissions
          lastModifiedTime
          lastModifiedBy
          entityMemberCount
          userEmails
        }
      }
      pageInfo {
        totalCount
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

export const LIST_ROLES = gql`
  query listRoles($cursor: Cursor) {
    listRoles(cursor: $cursor) {
      edges {
        node {
          id
          orgId
          name
          description
          isBuiltin
          permissions
          precedence
          lastModifiedTime
          lastModifiedBy
          lastModifiedByEmail
        }
      }
      pageInfo {
        totalCount
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

export const LIST_PERMISSIONS = gql`
  query listPermissions {
    listPermissions {
      permissions {
        id
        description
        category
      }
    }
  }
`;

export const GET_RESOURCE_PERMISSIONS = gql`
  query getResourcePermissions($resourceType: AcrResourceType!, $resourceId: String) {
    getResourcePermissions(resourceType: $resourceType, resourceId: $resourceId) {
      resourceType
      resourceId
      permissions
    }
  }
`;

export const LIST_IDP_GROUP_MAPPINGS = gql`
  query listIDPGroupMappings {
    listIDPGroupMappings {
      mappings {
        id
        idpGroupName
        securityGroupId
        securityGroupName
        lastSyncTime
        createdTime
        createdBy
      }
    }
  }
`;

export const QL_INVITE_USER = gql`
  mutation inviteUser($args: InviteOrgUserInput!) {
    inviteUser(args: $args) {
      user {
        id
        orgId
        email
        displayName
        status
        createdTime
        lastSeenTime
        lastModifiedTime
        accessibleAssetCount
      }
    }
  }
`;

export const QL_SET_ORG_USER_STATUS = gql`
  mutation setOrgUserStatus($args: SetOrgUserStatusInput!) {
    setOrgUserStatus(args: $args) {
      user {
        id
        status
        lastModifiedTime
      }
    }
  }
`;

export const QL_REMOVE_ORG_USER = gql`
  mutation removeOrgUser($args: RemoveOrgUserInput!) {
    removeOrgUser(args: $args) {
      userId
      success
    }
  }
`;

export const QL_DELETE_SECURITY_GROUP = gql`
  mutation deleteSecurityGroup($args: DeleteSecurityGroupInput!) {
    deleteSecurityGroup(args: $args) {
      securityGroupId
      success
    }
  }
`;

export const QL_DELETE_CUSTOM_ROLE = gql`
  mutation deleteCustomRole($args: DeleteCustomRoleInput!) {
    deleteCustomRole(args: $args) {
      roleId
      success
    }
  }
`;

export const QL_CREATE_CUSTOM_ROLE = gql`
  mutation createCustomRole($args: CreateCustomRoleInput!) {
    createCustomRole(args: $args) {
      role {
        id
        orgId
        name
        description
        isBuiltin
        permissions
        precedence
        lastModifiedTime
        lastModifiedBy
        lastModifiedByEmail
      }
    }
  }
`;

export const QL_UPDATE_CUSTOM_ROLE = gql`
  mutation updateCustomRole($args: UpdateCustomRoleInput!) {
    updateCustomRole(args: $args) {
      role {
        id
        orgId
        name
        description
        isBuiltin
        permissions
        precedence
        lastModifiedTime
        lastModifiedBy
        lastModifiedByEmail
      }
    }
  }
`;

export const QL_CREATE_ACR = gql`
  mutation createACR($args: CreateAcrInput!) {
    createACR(args: $args) {
      acr {
        id
        entityType
        entityId
        entityName
        roleId
        roleName
        resourceType
        resourceId
        resourceName
        lastModifiedTime
        entityMemberCount
      }
    }
  }
`;

export const QL_DELETE_ACR = gql`
  mutation deleteACR($args: DeleteAcrInput!) {
    deleteACR(args: $args) {
      acrId
      success
    }
  }
`;

export const QL_BULK_DELETE_ACRS = gql`
  mutation bulkDeleteACRs($args: BulkDeleteAcrsInput!) {
    bulkDeleteACRs(args: $args) {
      success
    }
  }
`;

export const QL_REPLACE_ACR = gql`
  mutation replaceACR($args: ReplaceAcrInput!) {
    replaceACR(args: $args) {
      replacedAcr {
        id
      }
      newAcr {
        id
        entityType
        entityId
        entityName
        roleId
        roleName
        resourceType
        resourceId
        resourceName
        lastModifiedTime
        entityMemberCount
      }
    }
  }
`;

export const QL_CREATE_SECURITY_GROUP = gql`
  mutation createSecurityGroup($args: CreateSecurityGroupInput!) {
    createSecurityGroup(args: $args) {
      securityGroup {
        id
        name
        description
        createdTime
        lastModifiedTime
        memberCount
        lastSeenTime
      }
    }
  }
`;

export const QL_ADD_SECURITY_GROUP_MEMBER = gql`
  mutation addSecurityGroupMember($args: AddSecurityGroupMemberInput!) {
    addSecurityGroupMember(args: $args) {
      member {
        userId
        email
        displayName
      }
    }
  }
`;

export const QL_REMOVE_SECURITY_GROUP_MEMBER = gql`
  mutation removeSecurityGroupMember($args: RemoveSecurityGroupMemberInput!) {
    removeSecurityGroupMember(args: $args) {
      success
    }
  }
`;

export const QL_UPDATE_SECURITY_GROUP = gql`
  mutation updateSecurityGroup($args: UpdateSecurityGroupInput!) {
    updateSecurityGroup(args: $args) {
      securityGroup {
        id
        name
        description
        createdTime
        lastModifiedTime
        memberCount
        lastSeenTime
      }
    }
  }
`;
